import dayjs from 'dayjs';
import { handleChange, resetInputDateOfBirth } from 'feature/form/formSlice';
import { resetMember } from 'feature/members/memberSlice';
import { resetMembers, searchMembers } from 'feature/members/membersSlice';
import { usePrevious } from 'hooks/previous.hook';
import { useCheckEligibility } from 'hooks/useCarrierCustomization';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/store';
import Box from 'styles/box/Box';
import Button from 'styles/button/Button';
import DatePickerField from 'styles/datePicker/DatePickerField';
import Stack from 'styles/stack/Stack';
import TextField from 'styles/textfield/TextField';
import palette from 'styles/theme/Palette';
import { IClient } from 'types/Carrier';
import { useTranslation } from 'utils/translations';

type TValues = {
	firstName: string | null;
	lastName: string | null;
	inputDateOfBirth: dayjs.Dayjs | null;
};

const FormMemberData: FC<{ selectedClient: IClient }> = ({
	selectedClient,
}) => {
	const [disabledSubmit, setDisabledSubmit] = useState<boolean>(true);
	const dispatch = useDispatch<any>();
	const [value, setValue] = useState<TValues>({
		firstName: null,
		lastName: null,
		inputDateOfBirth: null,
	});
	const prevUserData = usePrevious<TValues>(value);
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: 'clientLabels',
	});
	const { dateOfService } = useSelector((store: RootState) => store.form);
	const prevInputDateOfBirth = usePrevious(value.inputDateOfBirth);
	const { members } = useSelector((store: RootState) => store.members);
	const { checkEligibilityValidDate } = useCheckEligibility();

	const showCta = useMemo(() => !members || members?.length === 0, [members]);
	const isDateofServiceValid = checkEligibilityValidDate(selectedClient);

	const handleOnSubmit = (e: React.SyntheticEvent) => {
		e.preventDefault();
		if (!isDateofServiceValid) {
			dispatch(
				handleChange<any>({
					name: 'isDateOfServiceValid',
					value: isDateofServiceValid,
				}),
			);
			return;
		}
		const target = e.target as typeof e.target & {
			inputFirstName: { value: string };
			inputLastName: { value: string };
		};
		dispatch(
			searchMembers({
				values: {
					size: 10,
					dateOfService: dateOfService,
					firstName: target.inputFirstName.value,
					lastName: target.inputLastName.value,
					birthDate: dayjs(value.inputDateOfBirth).format('YYYY-MM-DD'),
				},
				carrierCode: selectedClient.carrier.code,
				externalClientId: selectedClient.externalClientId,
			}),
		);
	};

	useEffect(() => {
		if (
			!!value?.firstName &&
			!!value?.lastName &&
			!!value?.inputDateOfBirth &&
			!!dateOfService
		) {
			setDisabledSubmit(false);
		} else {
			setDisabledSubmit(true);
		}
	}, [value, dateOfService, value.inputDateOfBirth]);

	useEffect(() => {
		if (
			members?.length &&
			members?.length > 0 &&
			(prevUserData?.firstName !== value.firstName ||
				prevUserData?.lastName !== value.lastName ||
				!prevInputDateOfBirth?.isSame(value.inputDateOfBirth))
		) {
			dispatch(resetMembers());
			dispatch(resetMember());
		}
	}, [
		dispatch,
		value.inputDateOfBirth,
		members?.length,
		prevInputDateOfBirth,
		prevUserData,
		value,
	]);

	useEffect(() => {
		return () => dispatch(resetInputDateOfBirth());
	}, [dispatch]);

	return (
		<Box component={'form'} onSubmit={handleOnSubmit}>
			<Stack>
				<Stack rowGap={{ sm: 2.36375, lg: 3.615 }}>
					<TextField
						autoComplete={'off'}
						name="inputFirstName"
						type="text"
						label={tClientLabels('check-and-submit.searchLabels.firstName')}
						error={members?.length === 0}
						sx={{
							borderColor:
								members?.length === 0
									? palette.error.main
									: value.firstName?.length
										? palette.primary.main
										: 'none',
						}}
						onChange={e =>
							setValue(state => ({ ...state, firstName: e.target.value }))
						}
					/>
					<TextField
						autoComplete={'off'}
						name="inputLastName"
						type="text"
						label={tClientLabels('check-and-submit.searchLabels.lastName')}
						error={members?.length === 0}
						sx={{
							borderColor:
								members?.length === 0
									? palette.error.main
									: value.lastName?.length
										? palette.primary.main
										: 'none',
						}}
						onChange={e =>
							setValue(state => ({ ...state, lastName: e.target.value }))
						}
					/>
					<DatePickerField
						value={value.inputDateOfBirth}
						onChange={(date: dayjs.Dayjs | null) =>
							setValue(state => ({ ...state, inputDateOfBirth: date }))
						}
						label={tClientLabels('check-and-submit.searchLabels.dateOfBirth')}
						error={members?.length === 0}
						errorText={tClientLabels('check-and-submit.errors.memberData')}
						maxDate={dayjs()}
					/>
				</Stack>
				{showCta && (
					<Button
						variant="contained"
						type="submit"
						sx={{
							width: { sm: 112, lg: 116 },
							justifyContent: 'center',
						}}
						disabled={disabledSubmit}
					>
						{tClientLabels('commons.buttons.search')}
					</Button>
				)}
			</Stack>
		</Box>
	);
};

export default FormMemberData;
